import { useEffect, useId, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import cx from 'classnames';

import styles from './LanguageSwitcher.module.scss';

import { applyLocale } from '~/common/i18n';

const LOCALE_OPTIONS = [
  { code: 'en', flagSrc: '/icons/flags/en.svg', flagAlt: 'English' },
  { code: 'ka', flagSrc: '/icons/flags/ka.svg', flagAlt: 'Georgian' },
] as const;

type TLocaleCode = (typeof LOCALE_OPTIONS)[number]['code'];

type TLanguageSwitcherProps = {
  activeLocale: string;
  className?: string;
  onLocaleChange: (locale: string) => void | Promise<void>;
};

export function LanguageSwitcher({
  activeLocale,
  className,
  onLocaleChange,
}: TLanguageSwitcherProps) {
  const { t } = useTranslation('layout');
  const listId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const selectedLocale: TLocaleCode = LOCALE_OPTIONS.some(
    (option) => option.code === activeLocale,
  )
    ? (activeLocale as TLocaleCode)
    : LOCALE_OPTIONS[0].code;

  const selectedOption =
    LOCALE_OPTIONS.find((option) => option.code === selectedLocale) ??
    LOCALE_OPTIONS[0];

  const handleChange = async (locale: TLocaleCode) => {
    const resolved = applyLocale(locale);
    await Promise.resolve(onLocaleChange(resolved));
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (
        target instanceof Node &&
        rootRef.current &&
        !rootRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  return (
    <div className={cx(styles.root, className)} ref={rootRef}>
      <button
        aria-controls={listId}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={t('language.ariaLabel')}
        className={styles.trigger}
        onClick={() => setIsOpen((open) => !open)}
        type="button"
      >
        <img
          alt=""
          className={styles.flag}
          height={14}
          src={selectedOption.flagSrc}
          width={20}
        />
        <span className={styles.label}>{t(`language.${selectedLocale}`)}</span>
        <span aria-hidden className={styles.chevron} />
      </button>

      {isOpen ? (
        <ul className={styles.menu} id={listId} role="listbox">
          {LOCALE_OPTIONS.map((option) => (
            <li key={option.code} role="presentation">
              <button
                aria-selected={option.code === selectedLocale}
                className={cx(
                  styles.option,
                  option.code === selectedLocale && styles.optionSelected,
                )}
                onClick={() => handleChange(option.code)}
                role="option"
                type="button"
              >
                <img
                  alt={option.flagAlt}
                  className={styles.flag}
                  height={14}
                  src={option.flagSrc}
                  width={20}
                />
                <span className={styles.label}>
                  {t(`language.${option.code}`)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
